/**
 * Read-only inspector for the persona's composed prompt (Wave B #1).
 *
 * Shows the SAVED bundle's composition: structured sections in true
 * composition order with per-section token counts and jump-to-edit, the
 * runtime-layer strip, and the exact raw string. Unsaved edits are not
 * rendered — the dirty hint says so (Q3).
 *
 * Uses raw Radix Dialog rather than ui/Modal: ModalContent is not a flex
 * column and ModalBody hard-caps its height, which a tabbed full-height
 * panel needs to control itself. Same reason BlockFocusEditor bypasses it.
 */
import { useEffect, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Copy, Pencil, X } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { Button } from '@/components/ui/Button'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui'
import { useComposerStore } from '@/stores/composerStore'
import { usePersonaComposedPrompt } from '@/hooks/usePersonaComposedPrompt'
import { usePersonaRecentConversations } from '@/hooks/usePersonaRecentConversations'
import { usePromptInventory } from '@/hooks/usePromptInventory'
import { usePromptSnapshotList, usePromptSnapshotDetail } from '@/hooks/usePromptSnapshots'
import { usePermission } from '@/hooks/usePermission'
import { useFocusedBlock } from './FocusedBlockContext'
import { focusTargetForBundleIndex, focusTargetAcrossSnapshots } from './composedPromptTargets'
import { blockFieldLabel } from './blockTaxonomy'
import {
  RUNTIME_LAYER_COPY,
  RUNTIME_STRIP_INTRO,
  RENDERED_FOR_BUNDLE_ONLY_LABEL,
  RECONSTRUCTION_HONESTY_LABEL,
  SYSTEM_LAYERS_INTRO,
  CURRENT_STATE_RECONSTRUCTION_LABEL,
  CAPTURED_LABEL_TEXT,
  SNAPSHOT_LOAD_ERROR_TEXT,
  snapshotFooterText,
} from './workshopCopy'
import type { BreakdownSection } from '@/types/composedPrompt'
import type { PromptInventoryEntry, PromptInventoryStage } from '@/types/promptInventory'
import type { PromptSnapshotSection } from '@/types/promptSnapshot'

/** Fixed display order — extraction runs first, then render, then post. */
const STAGE_ORDER: PromptInventoryStage[] = ['extraction', 'render', 'post']
const STAGE_HEADING: Record<PromptInventoryStage, string> = {
  extraction: 'Extraction',
  render: 'Render',
  post: 'Post',
}

/** Radix Select has no null value — this sentinel means "saved bundle only". */
const BUNDLE_ONLY_VALUE = '__none__'

/** Radix Select has no null value — this sentinel means "no real snapshot
 *  selected, fall back to the Task 8 reconstruction". */
const CURRENT_STATE_VALUE = '__current__'

interface Props {
  open: boolean
  onClose: () => void
  personaId: string
  bundleId: string | null
  /** Workshop has unsaved edits — the inspector still shows the saved bundle. */
  dirty: boolean
}

export function ComposedPromptInspector({ open, onClose, personaId, bundleId, dirty }: Props) {
  const [tab, setTab] = useState('structured')
  const [copied, setCopied] = useState(false)
  // Block clicked in the breakdown no longer exists at that identity in the
  // live draft (deleted since save) — surfaced inline instead of opening
  // the wrong block or failing silently. Reset on every fresh opening so a
  // stale notice from a previous session doesn't linger.
  const [missingInDraft, setMissingInDraft] = useState(false)
  useEffect(() => {
    if (open) setMissingInDraft(false)
  }, [open])
  // "Rendered for" picker: null = saved bundle only (the original behavior).
  // Reset on close so a stale selection doesn't linger into the next open,
  // same reasoning as missingInDraft above.
  const [conversationId, setConversationId] = useState<string | null>(null)
  useEffect(() => {
    if (!open) setConversationId(null)
  }, [open])
  // Turn picker (Task 16): which captured snapshot (if any) is selected in
  // place of the Task 8 reconstruction. Reset whenever the conversation
  // changes (a snapshot id from a different conversation is meaningless)
  // and on close, same reasoning as `conversationId` above.
  const [snapshotId, setSnapshotId] = useState<string | null>(null)
  useEffect(() => {
    setSnapshotId(null)
  }, [conversationId])
  useEffect(() => {
    if (!open) setSnapshotId(null)
  }, [open])
  const serverBundle = useComposerStore((s) => s.serverBundle)
  const base = useComposerStore((s) => s.draftBundle ?? s.serverBundle)
  const { open: openBlock } = useFocusedBlock()
  const query = usePersonaComposedPrompt({ personaId, bundleId, conversationId, enabled: open })
  const recentConversations = usePersonaRecentConversations(personaId, open).data ?? []
  const inventoryEntries = usePromptInventory({ enabled: open }).data?.entries ?? []
  const canReadSnapshots = usePermission('prompt-snapshots:read')
  const snapshotList =
    usePromptSnapshotList({ personaId, conversationId, enabled: open && canReadSnapshots }).data ?? []
  const snapshotDetailQuery = usePromptSnapshotDetail({ personaId, snapshotId })
  const snapshotDetail = snapshotDetailQuery.data ?? null
  const showTurnPicker = Boolean(conversationId) && canReadSnapshots && snapshotList.length > 0
  // `snapshotMode`: a real turn is picked, REGARDLESS of whether its detail
  // has loaded yet — drives which content path renders (never falls back
  // to the reconstruction gates below). `snapshotSelected`: the detail is
  // actually ready to show.
  const snapshotMode = Boolean(snapshotId)
  const snapshotSelected = snapshotMode && Boolean(snapshotDetail)

  if (!open) return null

  const data = query.data
  const breakdown = data?.breakdown ?? null

  const handleEdit = (bundleIndex: number) => {
    // The breakdown describes the SAVED bundle, but the focus editor
    // indexes into the LIVE bundle (draftBundle ?? serverBundle) — those
    // can diverge, so bundleIndex is resolved through a stable
    // (category, ordinal) identity rather than reused as a raw index.
    const target = focusTargetAcrossSnapshots(serverBundle?.blocks ?? [], base?.blocks ?? [], bundleIndex)
    if (!target) {
      // The user deleted this block in the unsaved draft. Do not open
      // anything — that would silently land on unrelated content.
      setMissingInDraft(true)
      return
    }
    setMissingInDraft(false)
    // FocusTarget is a single slot and two full-screen dialogs must not
    // stack — close this one before handing over.
    onClose()
    openBlock(target)
  }

  // Raw tab's copy target: the captured snapshot's verbatim text when one
  // is selected, otherwise the reconstruction (Task 8, unchanged).
  const rawText = snapshotId && snapshotDetail ? snapshotDetail.system_text : data?.rendered_prompt

  const handleCopy = async () => {
    if (!rawText) return
    await navigator.clipboard.writeText(rawText)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }

  // The button LABEL describes the SAVED composition the inspector renders
  // (unlike handleEdit's click target, which intentionally resolves into
  // the live draft) — so its ordinal must come from `serverBundle`, never
  // from `base` (draftBundle ?? serverBundle). Reusing the live array here
  // was task-10's finding: it can invent an ordinal the saved composition
  // doesn't have (extra same-category blocks inserted upstream in the
  // draft), or collide two distinct saved blocks onto the same unsuffixed
  // label (a block missing from a shorter draft falls out of range).
  //
  // Returns null when `bundleIndex` doesn't resolve against `serverBundle`
  // at all — the breakdown response and the store's `serverBundle` disagree
  // about what was saved (a data-desync, not the normal draft-divergence
  // case this fix targets). `?? 0` would silently relabel that block as
  // "the first occurrence," which is the same fabrication being fixed here,
  // so callers must treat null explicitly instead.
  const ordinalFor = (bundleIndex: number): number | null =>
    focusTargetForBundleIndex(serverBundle?.blocks ?? [], bundleIndex)?.ordinal ?? null

  // A bundle-less (legacy) persona composes to ONE section holding its own
  // hand-authored rag_config.system_prompt. It has no editable block behind it,
  // so the backend types its origin "runtime" — but calling the most
  // human-authored text in the prompt "added at runtime" reads backwards.
  const originChip = (section: BreakdownSection) => {
    if (section.category === 'fallback_system_prompt') return 'legacy prompt'
    return section.origin === 'authored' ? 'authored' : 'added at runtime'
  }

  // A captured snapshot's sections come from the engine's ACTUAL composed
  // prompt for that turn, not the reconstruction breakdown — so this reuses
  // neither `originChip` above nor `RUNTIME_LAYER_COPY`. `workflow_context`
  // specifically is called out as "engine turn context" (it's the
  // per-turn guardrails/facts/agenda block the honesty label warns is
  // otherwise NOT reconstructable) rather than the generic "runtime".
  const snapshotSectionOrigin = (section: PromptSnapshotSection) => {
    if (section.origin === 'authored') return 'authored'
    if (section.category === 'workflow_context') return 'engine turn context'
    return 'runtime'
  }

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs data-[state=open]:animate-fade-in" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 flex h-[86vh] w-[92vw] max-w-6xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-background shadow-2xl"
        >
          <div className="flex items-center gap-3 border-b border-border px-5 py-3.5">
            <Dialog.Title className="text-base font-semibold text-foreground">
              Composed prompt
            </Dialog.Title>
            {data?.target_format ? (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                {data.target_format}
              </span>
            ) : null}
            <div data-testid="rendered-for-picker">
              <Select
                value={conversationId ?? BUNDLE_ONLY_VALUE}
                onValueChange={(v) => setConversationId(v === BUNDLE_ONLY_VALUE ? null : v)}
              >
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={BUNDLE_ONLY_VALUE}>{RENDERED_FOR_BUNDLE_ONLY_LABEL}</SelectItem>
                  {recentConversations.map((conv) => (
                    <SelectItem key={conv.id} value={conv.id}>
                      {conv.channel} · {conv.last_message_snippet || conv.id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close composed prompt"
              className="ml-auto rounded-sm p-1.5 text-muted-foreground hover:bg-muted-foreground/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {conversationId ? (
            <div className="px-5 pt-3">
              {snapshotSelected ? (
                <p
                  data-testid="captured-label"
                  className="rounded border border-dashed border-success/50 bg-success/10 px-3 py-2 text-xs text-muted-foreground"
                >
                  {CAPTURED_LABEL_TEXT}
                </p>
              ) : snapshotMode ? (
                // A real turn IS picked but its detail hasn't resolved
                // (still loading, or errored) — the content area below
                // shows the loading hint / error message; showing the
                // reconstruction's honesty label here would be actively
                // wrong (this isn't a reconstruction, and isn't "current
                // state" either).
                null
              ) : (
                <p
                  data-testid="reconstruction-label"
                  className="rounded border border-dashed border-warning/50 bg-warning/10 px-3 py-2 text-xs text-muted-foreground"
                >
                  {RECONSTRUCTION_HONESTY_LABEL.beforeCurrent}
                  <strong>{RECONSTRUCTION_HONESTY_LABEL.current}</strong>
                  {RECONSTRUCTION_HONESTY_LABEL.betweenCurrentAndNot}
                  <strong>{RECONSTRUCTION_HONESTY_LABEL.not}</strong>
                  {RECONSTRUCTION_HONESTY_LABEL.after}
                </p>
              )}
            </div>
          ) : null}

          {showTurnPicker ? (
            <div className="px-5 pt-3">
              <div data-testid="turn-picker">
                <Select
                  value={snapshotId ?? CURRENT_STATE_VALUE}
                  onValueChange={(v) => setSnapshotId(v === CURRENT_STATE_VALUE ? null : v)}
                >
                  <SelectTrigger className="w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CURRENT_STATE_VALUE}>
                      {CURRENT_STATE_RECONSTRUCTION_LABEL}
                    </SelectItem>
                    {snapshotList.map((snap) => (
                      <SelectItem key={snap.id} value={snap.id}>
                        {snap.node || snap.source} · {new Date(snap.created_at).toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          {dirty ? (
            <div className="border-b border-border bg-warning/10 px-5 py-2 text-xs text-warning-strong">
              Showing the last saved version. Save to refresh.
            </div>
          ) : null}

          {missingInDraft ? (
            <div
              data-testid="inspector-missing-block-notice"
              aria-live="polite"
              className="border-b border-border bg-warning/10 px-5 py-2 text-xs text-warning-strong"
            >
              That block no longer exists in your unsaved draft. Save your changes or discard the
              draft to edit it again.
            </div>
          ) : null}

          {snapshotMode && snapshotDetailQuery.isLoading ? (
            // A real turn is picked and its detail is still in flight — the
            // RECONSTRUCTION query's state (loading/error/success) is
            // irrelevant here (fix: it must never gate the snapshot path;
            // the current bundle failing to recompose is exactly why a
            // capture gets reached for).
            <div data-testid="snapshot-loading" className="flex-1 p-5 text-sm text-muted-foreground">
              Loading snapshot…
            </div>
          ) : snapshotMode && (snapshotDetailQuery.isError || !snapshotDetail) ? (
            // `retry: false` makes a detail error permanent for this
            // render — say so plainly instead of silently falling back to
            // the reconstruction while the turn picker still shows this
            // snapshot as selected (that combination would look like a
            // bug, not an error).
            <div data-testid="snapshot-load-error" className="flex-1 p-5 text-sm text-destructive">
              {SNAPSHOT_LOAD_ERROR_TEXT}
            </div>
          ) : !snapshotMode && query.isLoading ? (
            <div className="flex-1 p-5 text-sm text-muted-foreground">Composing…</div>
          ) : !snapshotMode && (query.isError || !breakdown) ? (
            <div className="flex-1 p-5 text-sm text-destructive">
              Couldn’t build the composed prompt. Close and try again.
            </div>
          ) : (
            <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
              <div className="px-5 pt-3">
                <TabsList>
                  <TabsTrigger value="structured">Structured</TabsTrigger>
                  <TabsTrigger value="raw">Raw</TabsTrigger>
                  <TabsTrigger value="system">System layers</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="structured" className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                {snapshotSelected && snapshotDetail ? (
                  <div data-testid="snapshot-structured">
                    <div className="flex flex-wrap gap-2">
                      {snapshotDetail.sections.map((section) => (
                        <span
                          key={section.category}
                          data-testid={`snapshot-chip-${section.category}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-foreground"
                        >
                          {section.category}
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                            {snapshotSectionOrigin(section)}
                          </span>
                          <span className="text-muted-foreground">{section.char_count} chars</span>
                        </span>
                      ))}
                    </div>
                    <pre className="mt-3 whitespace-pre-wrap rounded-md border border-border bg-muted p-3 text-xs text-foreground">
                      {snapshotDetail.system_text}
                    </pre>
                  </div>
                ) : breakdown ? (
                  <>
                    {breakdown.sections.map((section) => (
                      <div
                        key={`${section.category}-${section.label}`}
                        data-testid={`inspector-section-${section.category}`}
                        className="rounded-lg border border-border"
                      >
                        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/50 px-3 py-2">
                          <span
                            data-testid="inspector-section-label"
                            className="text-sm font-semibold text-foreground"
                          >
                            {section.label}
                          </span>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                            {originChip(section)}
                          </span>
                          <span className="ml-auto text-xs text-muted-foreground">
                            ≈ {section.token_estimate} tokens
                          </span>
                        </div>

                        {section.origin === 'authored' ? (
                          <div className="flex flex-wrap gap-2 border-b border-border px-3 py-2">
                            {section.blocks.map((block) => {
                              if (block.bundle_index === null) return null
                              const ordinal = ordinalFor(block.bundle_index)
                              // ordinal === null: see ordinalFor's doc comment.
                              // Flag it plainly rather than falling back to the
                              // unsuffixed "first occurrence" label.
                              const label =
                                ordinal === null
                                  ? `${blockFieldLabel(block.category, 0)} (?)`
                                  : blockFieldLabel(block.category, ordinal)
                              return (
                                <button
                                  key={block.bundle_index}
                                  type="button"
                                  onClick={() => handleEdit(block.bundle_index as number)}
                                  aria-label={`Edit ${label}`}
                                  className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted"
                                >
                                  <Pencil className="h-3 w-3" aria-hidden />
                                  {label}
                                  <span className="text-muted-foreground">≈ {block.token_estimate}</span>
                                </button>
                              )
                            })}
                          </div>
                        ) : null}

                        <pre className="max-h-64 overflow-auto whitespace-pre-wrap px-3 py-2 text-xs text-foreground">
                          {section.text}
                        </pre>
                      </div>
                    ))}

                    <div data-testid="runtime-layer-strip" className="rounded-lg border border-dashed border-border p-3">
                      <p className="mb-2 text-xs text-muted-foreground">{RUNTIME_STRIP_INTRO}</p>
                      <ul className="space-y-1">
                        {breakdown.runtime_layers.map((layer) => (
                          <li
                            key={layer.category}
                            data-layer={layer.category}
                            data-present={String(layer.present)}
                            className="flex flex-wrap items-baseline gap-2 text-xs"
                          >
                            <span className="font-medium text-foreground">
                              {RUNTIME_LAYER_COPY[layer.category]?.label ?? layer.category}
                            </span>
                            <span className="text-muted-foreground">
                              {RUNTIME_LAYER_COPY[layer.category]?.description ?? ''}
                            </span>
                            <span className="ml-auto text-muted-foreground">
                              {layer.present ? 'in this render' : 'added per conversation'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : null}
              </TabsContent>

              <TabsContent value="raw" className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                <div className="mb-2 flex items-center gap-2">
                  <Button size="sm" variant="secondary" onClick={handleCopy}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Copy
                  </Button>
                  <span aria-live="polite" className="text-xs text-muted-foreground">
                    {copied ? 'Copied' : ''}
                  </span>
                </div>
                <pre className="whitespace-pre-wrap rounded-md border border-border bg-muted p-3 text-xs text-foreground">
                  {rawText}
                </pre>
              </TabsContent>

              <TabsContent value="system" className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                <p className="text-xs text-muted-foreground">{SYSTEM_LAYERS_INTRO}</p>
                {STAGE_ORDER.map((stage) => {
                  const stageEntries = inventoryEntries.filter((entry) => entry.stage === stage)
                  if (stageEntries.length === 0) return null
                  return (
                    <div key={stage} className="space-y-2">
                      <h3 className="text-sm font-semibold text-foreground">{STAGE_HEADING[stage]}</h3>
                      {stageEntries.map((entry: PromptInventoryEntry) => (
                        <div key={entry.id} className="rounded-lg border border-border">
                          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/50 px-3 py-2">
                            <span className="text-sm font-semibold text-foreground">{entry.id}</span>
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                              {entry.kind}
                            </span>
                            <code className="text-xs text-muted-foreground">{entry.source}</code>
                          </div>
                          {entry.note ? (
                            <p className="border-b border-border px-3 py-1.5 text-xs text-muted-foreground">
                              {entry.note}
                            </p>
                          ) : null}
                          <pre className="whitespace-pre-wrap px-3 py-2 text-xs text-foreground">
                            {entry.text}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )
                })}
              </TabsContent>

              <div className="border-t border-border px-5 py-2.5 text-xs text-muted-foreground">
                {snapshotSelected && snapshotDetail ? (
                  snapshotFooterText(snapshotDetail.system_text, snapshotDetail.sections.length)
                ) : breakdown ? (
                  <>
                    ≈ {breakdown.total_token_estimate} tokens total ·{' '}
                    {breakdown.formatting_overhead_tokens} in formatting (section headers and spacing)
                  </>
                ) : null}
              </div>
            </Tabs>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
